import { useCallback, useEffect } from 'react';
import useRealtimeEventStream, { REALTIME_EVENT_TYPES } from './useRealtimeEventStream';
import { invalidateDesktopTransactionSnapshots } from '../services/desktopTransactionSnapshots';
import {
  publishFinanceTrendDataChange,
  publishNetWorthTrendDataChange,
} from '../utils/trendDataInvalidation';

const desktopRealtimeListeners = new Set();

const publishDesktopRealtimeEvent = (event) => {
  desktopRealtimeListeners.forEach((listener) => listener(event));
};

const subscribeDesktopRealtimeEvent = (listener) => {
  desktopRealtimeListeners.add(listener);
  return () => desktopRealtimeListeners.delete(listener);
};

export const resolveGlobalTrendsRealtimePlan = ({ event, householdId } = {}) => {
  if (!householdId || !event?.type) {
    return { invalidateFinance: false, invalidateNetWorth: false };
  }
  if (event.householdId && String(event.householdId) !== String(householdId)) {
    return { invalidateFinance: false, invalidateNetWorth: false };
  }
  return {
    invalidateFinance: event.type === REALTIME_EVENT_TYPES.FINANCE_DATA_CHANGED,
    invalidateNetWorth: event.type === REALTIME_EVENT_TYPES.NET_WORTH_CHANGED,
  };
};

export const useDesktopRealtimeInvalidation = ({
  authenticatedFetch,
  enabled,
  householdId,
}) => {
  const handleRealtimeEvent = useCallback((event) => {
    const plan = resolveGlobalTrendsRealtimePlan({ event, householdId });
    if (!plan.invalidateFinance && !plan.invalidateNetWorth
      && event?.householdId && String(event.householdId) !== String(householdId)) {
      return;
    }
    if (plan.invalidateFinance) {
      publishFinanceTrendDataChange({ householdId, background: true,
        ...(event.categoriesChanged !== true && Array.isArray(event.monthKeys) ? { monthKeys: event.monthKeys } : {}) });
    }
    if (plan.invalidateNetWorth) {
      publishNetWorthTrendDataChange({ householdId });
    }
    if (event?.type === REALTIME_EVENT_TYPES.REVIEW_QUEUE_CHANGED) {
      invalidateDesktopTransactionSnapshots(householdId, { resources: ['review'] });
    }
    publishDesktopRealtimeEvent(event);
  }, [householdId]);

  useRealtimeEventStream({
    authenticatedFetch,
    householdId,
    enabled,
    onEvent: handleRealtimeEvent,
  });
};

const emptyPlan = Object.freeze({
  handleReviewQueueEvent: false,
  refreshFinance: false,
  refreshBankState: false,
});

export const resolveDashboardRealtimeRefreshPlan = ({
  event,
  householdId,
  selectedMonth,
} = {}) => {
  if (!householdId || !event?.type) return emptyPlan;
  if (event.householdId && String(event.householdId) !== String(householdId)) {
    return emptyPlan;
  }

  if (event.type === REALTIME_EVENT_TYPES.REVIEW_QUEUE_CHANGED) {
    return {
      handleReviewQueueEvent: true,
      refreshFinance: false,
      refreshBankState: false,
    };
  }

  if (event.type !== REALTIME_EVENT_TYPES.FINANCE_DATA_CHANGED) {
    return emptyPlan;
  }

  const monthKeys = Array.isArray(event.monthKeys)
    ? event.monthKeys.map((value) => String(value || '').trim()).filter(Boolean)
    : [];
  const isRelevantMonth = monthKeys.length === 0 || monthKeys.includes(selectedMonth);
  const refreshBankState = event.bankStatusChanged === true;
  const refreshFinance = isRelevantMonth || event.categoriesChanged === true;

  if (!refreshFinance && !refreshBankState) return emptyPlan;

  return {
    handleReviewQueueEvent: false,
    refreshFinance,
    refreshBankState,
  };
};

const useDashboardRealtimeRefresh = ({
  enabled,
  fetchAlerts,
  fetchBankStatus,
  fetchConnectedAccounts,
  fetchDashboardData,
  handleReviewQueueEvent,
  householdId,
  refreshAvailableMonths,
  selectedMonth,
}) => {
  const handleRealtimeEvent = useCallback((event) => {
    const plan = resolveDashboardRealtimeRefreshPlan({
      event,
      householdId,
      selectedMonth,
    });

    if (plan.handleReviewQueueEvent) {
      handleReviewQueueEvent(event);
      return;
    }

    if (plan.refreshFinance) {
      void fetchDashboardData({
        includeCategories: event.categoriesChanged === true,
        includeTransactions: true,
        includeIncome: true,
        silent: true,
        reason: 'realtime-finance-sync',
      });
      void fetchAlerts();
      void refreshAvailableMonths();
    }

    if (plan.refreshBankState) {
      void fetchBankStatus();
      void fetchConnectedAccounts();
    }
  }, [
    fetchAlerts,
    fetchBankStatus,
    fetchConnectedAccounts,
    fetchDashboardData,
    handleReviewQueueEvent,
    householdId,
    refreshAvailableMonths,
    selectedMonth,
  ]);

  useEffect(() => {
    if (!enabled) return undefined;
    return subscribeDesktopRealtimeEvent(handleRealtimeEvent);
  }, [enabled, handleRealtimeEvent]);
};

export default useDashboardRealtimeRefresh;
